/**
 * PageHeader Component
 *
 * Consistent page header with title, description, breadcrumbs, and actions.
 *
 * GAP-007: Enhanced breadcrumb navigation
 * - Always visible (no hiding on mobile)
 * - Home icon for first item
 * - Proper ARIA labels and semantics
 * - Keyboard accessible
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, HomeIcon } from '@heroicons/react/24/outline';

import { cn } from '../../utils/cn';

export interface Breadcrumb {
  label: string;
  href?: string;
  /** Icon to display before label */
  icon?: React.ReactNode;
  /** Whether this is the current/active breadcrumb */
  current?: boolean;
}

export interface PageHeaderProps {
  /** Page title - can be string or ReactNode for custom layouts */
  title: string | ReactNode;
  /** Optional description below title */
  description?: string | ReactNode;
  /** Breadcrumb navigation */
  breadcrumbs?: Breadcrumb[];
  /** Action buttons to display on the right */
  actions?: ReactNode;
  /** Optional badge to display next to title (e.g., count) */
  badge?: ReactNode;
  /** Additional className */
  className?: string;
  /** Children rendered below the header */
  children?: ReactNode;
}

export function PageHeader({
  title,
  description,
  breadcrumbs,
  actions,
  badge,
  className,
  children,
}: PageHeaderProps) {
  return (
    <div className={cn('mb-6', className)}>
      {/* GAP-007: Enhanced Breadcrumbs - always visible, with home icon */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav className="mb-3" aria-label="Kruimelpad">
          <ol className="flex flex-wrap items-center gap-1 sm:gap-1.5 text-sm text-mv-gray-500">
            {breadcrumbs.map((crumb, idx) => {
              const isFirst = idx === 0;
              const isLast = idx === breadcrumbs.length - 1;
              const showHomeIcon = isFirst && crumb.href;

              return (
                <li key={idx} className="flex items-center gap-1 sm:gap-1.5">
                  {idx > 0 && (
                    <ChevronRightIcon
                      // GAP-015: text-mv-gray-500 for WCAG AA contrast
                      className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0 text-mv-gray-500"
                      aria-hidden="true"
                    />
                  )}
                  {crumb.href && !isLast ? (
                    <Link
                      to={crumb.href}
                      className={cn(
                        'flex items-center gap-1 rounded px-1 py-0.5 -mx-1',
                        'hover:text-mv-blue-600 hover:bg-mv-blue-50',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-mv-blue-500'
                      )}
                    >
                      {showHomeIcon && (
                        <HomeIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                      )}
                      {crumb.icon && !showHomeIcon && (
                        <span className="shrink-0">{crumb.icon}</span>
                      )}
                      <span className={cn(showHomeIcon && 'sr-only sm:not-sr-only')}>
                        {crumb.label}
                      </span>
                    </Link>
                  ) : (
                    <span
                      className="font-medium text-mv-gray-700 flex items-center gap-1"
                      aria-current={isLast ? 'page' : undefined}
                    >
                      {crumb.icon && <span className="shrink-0">{crumb.icon}</span>}
                      {crumb.label}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      )}

      {/* Title and actions row */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-mv-blue-900 sm:text-3xl">{title}</h1>
            {badge && (
              <span className="inline-flex items-center rounded-full bg-mv-blue-100 px-2.5 py-0.5 text-sm font-medium text-mv-blue-800">
                {badge}
              </span>
            )}
          </div>
          {description && (
            <p className="mt-1 text-mv-gray-600">{description}</p>
          )}
        </div>
        {actions && (
          <div className="flex shrink-0 flex-wrap items-center gap-3">{actions}</div>
        )}
      </div>

      {/* Optional children (tabs, filters, etc.) */}
      {children && <div className="mt-4">{children}</div>}
    </div>
  );
}
